import { useCallback, useRef, useEffect } from 'react'
import type { GenerationConfig } from '@/types/autoGeneration'

interface UseAutoGenerationSchedulerProps {
  isRecording: boolean
  shouldPauseGeneration: boolean
  config: GenerationConfig
  getCurrentTimeConfig: () => any
  getNextAvailableInterval: () => any
  canMakeCall: (type: string) => boolean
  addToQueue: (fn: (abortSignal?: AbortSignal) => Promise<any>, options: any) => Promise<any>
  generateTitleAPI: (abortSignal?: AbortSignal) => Promise<any>
  generateReportAPI: (abortSignal?: AbortSignal) => Promise<any>
  transcriptionsRef: React.MutableRefObject<any[]>
  recordingStartTime: React.MutableRefObject<Date | null>
  setIsGeneratingTitle: (value: boolean) => void
  setIsGeneratingReport: (value: boolean) => void
  setTitleError: (value: boolean) => void
  setReportError: (value: boolean) => void
  setLastTitleGenerated: (date: Date) => void
  setLastReportGenerated: (date: Date) => void
  setReportContent: (content: any) => void
  setProgressiveSections: (sections: string[]) => void
  canGenerateTitle: () => Promise<boolean>
}

export const useAutoGenerationScheduler = ({
  isRecording,
  shouldPauseGeneration,
  config,
  getCurrentTimeConfig,
  getNextAvailableInterval,
  canMakeCall,
  addToQueue,
  generateTitleAPI,
  generateReportAPI,
  transcriptionsRef,
  recordingStartTime,
  setIsGeneratingTitle,
  setIsGeneratingReport,
  setTitleError,
  setReportError,
  setLastTitleGenerated,
  setLastReportGenerated,
  setReportContent,
  setProgressiveSections,
  canGenerateTitle
}: UseAutoGenerationSchedulerProps) => {
  const schedulerRef = useRef<NodeJS.Timeout | null>(null)

  // Queue title generation using the LLM queue system
  const queueTitleGeneration = useCallback(async () => {
    // CRITICAL: Check pause condition at execution time
    if (!config.titleGeneration.enabled || shouldPauseGeneration) {
      console.log("🚫 [TITLE] Generation skipped - disabled or paused at execution time")
      return
    }

    if (!canMakeCall("title")) {
      console.log("🚫 [TITLE] Generation skipped - rate limited")
      return
    }

    // Check if title can be generated (not manually edited)
    const canGenerate = await canGenerateTitle()
    if (!canGenerate) {
      console.log("🚫 [TITLE] Generation skipped - title manually edited")
      return
    }

    try {
      console.log("📝 [TITLE] Queuing title generation...")
      setIsGeneratingTitle(true)
      setTitleError(false)

      const result = await addToQueue((abortSignal) => generateTitleAPI(abortSignal), {
        priority: config.titleGeneration.priority,
        maxRetries: config.titleGeneration.maxRetries,
        type: "title",
      })

      console.log("✅ [TITLE] Title generated successfully")
      setLastTitleGenerated(new Date())
    } catch (error) {
      if (error.message !== 'Call cancelled due to editing') {
        console.error("❌ [TITLE] Title generation failed:", error)
        setTitleError(true)
      }
    } finally {
      setIsGeneratingTitle(false)
    }
  }, [
    config.titleGeneration,
    shouldPauseGeneration,
    canMakeCall,
    canGenerateTitle,
    addToQueue,
    generateTitleAPI,
    setIsGeneratingTitle,
    setTitleError,
    setLastTitleGenerated
  ])

  // Queue report generation using the LLM queue system
  const queueReportGeneration = useCallback(async () => {
    // CRITICAL: Check pause condition at execution time
    if (!config.reportGeneration.enabled || shouldPauseGeneration) {
      console.log("🚫 [REPORT] Generation skipped - disabled or paused at execution time")
      return
    }

    if (!canMakeCall("report")) {
      console.log("🚫 [REPORT] Generation skipped - rate limited")
      return
    }

    try {
      console.log("📊 [REPORT] Queuing report generation...")
      setIsGeneratingReport(true)
      setReportError(false)

      const result = await addToQueue((abortSignal) => generateReportAPI(abortSignal), {
        priority: config.reportGeneration.priority,
        maxRetries: config.reportGeneration.maxRetries,
        type: "report",
      })

      console.log("✅ [REPORT] Report generated successfully")
      if (result.content) {
        setReportContent(result.content)
      }
      
      // Set progressive sections for display
      if (result.editedSections) {
        console.log("📊 [REPORT] Setting progressive sections:", result.editedSections)
        setProgressiveSections(result.editedSections)
      }
      
      setLastReportGenerated(new Date())
    } catch (error) {
      if (error.message !== 'Call cancelled due to editing') {
        console.error("❌ [REPORT] Report generation failed:", error)
        setReportError(true)
      }
    } finally {
      setIsGeneratingReport(false)
    }
  }, [
    config.reportGeneration,
    shouldPauseGeneration,
    canMakeCall,
    addToQueue,
    generateReportAPI,
    setIsGeneratingReport,
    setReportError,
    setLastReportGenerated,
    setReportContent,
    setProgressiveSections
  ])

  // Scheduler que programa tareas basado en disponibilidad de llamadas
  const scheduleNextGeneration = useCallback(() => {
    // Limpiar timeout anterior si existe
    if (schedulerRef.current) {
      clearTimeout(schedulerRef.current)
      schedulerRef.current = null
    }

    // VERIFICACIÓN CRÍTICA: Si no estamos grabando, PARAR completamente
    if (!isRecording) {
      console.log('🛑 [SCHEDULER] Recording stopped - stopping all generation')
      return
    }

    if (shouldPauseGeneration) {
      console.log('⏸️ [SCHEDULER] PAUSE DETECTED at execution time - stopping all generation')
      return
    }

    const currentConfig = getCurrentTimeConfig()
    if (!currentConfig) {
      // Si no hay configuración actual, buscar la próxima
      const nextInterval = getNextAvailableInterval()
      if (nextInterval && recordingStartTime.current) {
        const elapsedMinutes = (Date.now() - recordingStartTime.current.getTime()) / (1000 * 60)
        const minutesUntilNext = nextInterval.startMinute - elapsedMinutes
        
        if (minutesUntilNext > 0) {
          const delayMs = minutesUntilNext * 60 * 1000
          console.log(`⏰ [SCHEDULER] Next interval in ${minutesUntilNext.toFixed(1)} minutes`)
          schedulerRef.current = setTimeout(scheduleNextGeneration, delayMs)
        }
      }
      return
    }

    // Check if we have enough transcription content before scheduling
    const transcriptLength = transcriptionsRef.current
      .map(t => t.transcript || t.text || '')
      .join(' ').length

    console.log('🔍 [SCHEDULER] Current transcript length:', transcriptLength)

    if (transcriptLength < 20) {
      console.log('⏸️ [SCHEDULER] Not enough content yet, scheduling next check in 15 seconds')
      schedulerRef.current = setTimeout(scheduleNextGeneration, 15000) // Check again in 15 seconds
      return
    }

    // Ejecutar generación inmediatamente si hay contenido suficiente
    const executeGeneration = async () => {
      // VERIFICACIÓN CRÍTICA: Verificar condiciones al momento de ejecutar
      if (!isRecording) {
        console.log('🛑 [SCHEDULER] Recording stopped during execution - stopping')
        return
      }

      if (shouldPauseGeneration) {
        console.log('⏸️ [SCHEDULER] PAUSE DETECTED at execution time - stopping all generation')
        return
      }

      const currentTranscriptLength = transcriptionsRef.current
        .map(t => t.transcript || t.text || '')
        .join(' ').length

      if (currentTranscriptLength < 20) {
        console.log('⏸️ [SCHEDULER] Not enough content at execution time')
        // Solo programar siguiente verificación si aún estamos grabando
        if (isRecording && !shouldPauseGeneration) {
          schedulerRef.current = setTimeout(scheduleNextGeneration, currentConfig.callIntervalSeconds * 1000)
        }
        return
      }

      // Determinar si estamos en el primer minuto (donde títulos están permitidos)
      const elapsedMinutes = recordingStartTime.current ? 
        (Date.now() - recordingStartTime.current.getTime()) / (1000 * 60) : 0
      const isFirstMinute = elapsedMinutes < 1

      console.log(`⏰ [SCHEDULER] Elapsed minutes: ${elapsedMinutes.toFixed(2)}, isFirstMinute: ${isFirstMinute}`)

      // Verificar estado de rate limits independientemente para cada tipo
      const canCallTitle = canMakeCall("title")
      const canCallReport = canMakeCall("report")
      
      console.log(`🔍 [SCHEDULER] Independent rate limits check - Title: ${canCallTitle}, Report: ${canCallReport}`)
      console.log(`🔍 [SCHEDULER] Config enabled - Title: ${config.titleGeneration.enabled}, Report: ${config.reportGeneration.enabled}`)

      // Ejecutar ambas generaciones en paralelo si ambas están disponibles
      const promises: Promise<void>[] = []
      let titleExecuted = false
      let reportExecuted = false

      // TÍTULOS: Solo durante el primer minuto Y verificar pausa de nuevo
      if (config.titleGeneration.enabled && isFirstMinute && canCallTitle && !shouldPauseGeneration) {
        console.log('📝 [SCHEDULER] Executing title generation (first minute allowed, not paused)')
        promises.push(queueTitleGeneration())
        titleExecuted = true
      } else if (config.titleGeneration.enabled && shouldPauseGeneration) {
        console.log('⏸️ [SCHEDULER] Title generation skipped - PAUSED')
      } else if (config.titleGeneration.enabled && !isFirstMinute) {
        console.log('⏸️ [SCHEDULER] Title generation skipped - not in first minute')
      } else if (config.titleGeneration.enabled && !canCallTitle) {
        console.log('⏸️ [SCHEDULER] Title generation skipped - rate limited')
      }

      // REPORTES: Desde el minuto 0 en adelante Y verificar pausa de nuevo
      if (config.reportGeneration.enabled && canCallReport && !shouldPauseGeneration) {
        console.log('📊 [SCHEDULER] Executing report generation (allowed from start, not paused)')
        promises.push(queueReportGeneration())
        reportExecuted = true
      } else if (config.reportGeneration.enabled && shouldPauseGeneration) {
        console.log('⏸️ [SCHEDULER] Report generation skipped - PAUSED')
      } else if (config.reportGeneration.enabled && !canCallReport) {
        console.log('⏸️ [SCHEDULER] Report generation skipped - rate limited')
      }

      // Ejecutar ambas llamadas en paralelo si están disponibles
      if (promises.length > 0) {
        try {
          await Promise.allSettled(promises)
          console.log(`✅ [SCHEDULER] Executed in parallel - Title: ${titleExecuted}, Report: ${reportExecuted}`)
        } catch (error) {
          console.error('❌ [SCHEDULER] Error in parallel execution:', error)
        }
      } else {
        console.log('⏸️ [SCHEDULER] No calls available at execution time')
      }

      // VERIFICACIÓN CRÍTICA: Solo programar siguiente generación si aún estamos grabando
      if (isRecording && !shouldPauseGeneration && getCurrentTimeConfig()) {
        const nextDelay = currentConfig.callIntervalSeconds * 1000
        console.log(`⏰ [SCHEDULER] Next generation scheduled in ${currentConfig.callIntervalSeconds}s`)
        schedulerRef.current = setTimeout(scheduleNextGeneration, nextDelay)
      } else {
        console.log('🛑 [SCHEDULER] Not scheduling next generation - recording stopped or paused')
      }
    }

    // Ejecutar inmediatamente
    executeGeneration()
  }, [
    isRecording,
    shouldPauseGeneration,
    getCurrentTimeConfig,
    getNextAvailableInterval,
    canMakeCall,
    queueTitleGeneration,
    queueReportGeneration,
    transcriptionsRef,
    recordingStartTime,
    config.titleGeneration.enabled,
    config.reportGeneration.enabled
  ])

  // Cleanup cuando el componente se desmonta o cuando isRecording cambia
  useEffect(() => {
    if (!isRecording && schedulerRef.current) {
      console.log('🛑 [SCHEDULER] Clearing scheduler timeout - recording stopped')
      clearTimeout(schedulerRef.current)
      schedulerRef.current = null
    }

    return () => {
      if (schedulerRef.current) {
        console.log('🧹 [SCHEDULER] Cleaning up scheduler on unmount')
        clearTimeout(schedulerRef.current)
        schedulerRef.current = null
      }
    }
  }, [isRecording])

  return {
    scheduleNextGeneration,
    queueTitleGeneration,
    queueReportGeneration
  }
}
